import {
  MediaType,
  Upload,
  UploadStatus,
  toVideoMetadata,
} from '@buffer-mono/uploader'
import type { BufferUpload, FinishedUpload } from '@buffer-mono/uploader'

import { ImageDimensions } from '../values/ImageDimensions'

/**
 * The composer's upload, from what the upload service reported when the bytes
 * landed. The service classifies media by file extension and the uploader by
 * MIME type, so a GIF arrives as a photo here and stays a GIF on the upload.
 *
 * The `Upload.is*` guards narrow to the variant each branch returns, which is
 * what makes the fields it requires — `thumbnailUrl` on videos and GIFs — spread
 * through as non-optional.
 */
export function toBufferUpload(
  upload: Upload,
  finished: FinishedUpload,
): BufferUpload {
  const { location: url, uploadId } = finished
  // The service renders a thumbnail for media that cannot be its own, and
  // answers the source location for media that can. Spread rather than always
  // assigned so the narrowed variant keeps Uppy's client-side preview as the
  // non-optional `thumbnailUrl` when the record has none.
  const rendered = finished.thumbnail
    ? { thumbnailUrl: finished.thumbnail }
    : {}
  const isPhotoUpload = Upload.isImage(upload) || Upload.isGif(upload)
  const isVideoUpload = Upload.isVideo(upload)
  const isDocumentUpload = Upload.isDocumentUpload(upload)

  if (finished.type === 'photo' && isPhotoUpload) {
    return {
      ...upload,
      status: UploadStatus.Completed,
      url,
      uploadId,
      ...rendered,
      dimensions: ImageDimensions.new({
        width: finished.width,
        height: finished.height,
      }),
    }
  }

  if (finished.type === 'document' && isDocumentUpload) {
    return {
      ...upload,
      status: UploadStatus.Completed,
      mediaType: MediaType.document,
      url,
      uploadId,
      ...rendered,
      details: {
        title: upload.name,
        pageCount: finished.details.pageCount ?? null,
        fileSizeBytes: finished.details.fileSizeBytes,
      },
    }
  }

  if (finished.type === 'video' && isVideoUpload) {
    return {
      ...upload,
      status: UploadStatus.Completed,
      url,
      uploadId,
      ...rendered,
      videoMetadata: toVideoMetadata(finished.details),
    }
  }

  throw new Error(
    `Upload failed: the upload service reported ${finished.type} and the upload is ${upload.mediaType}`,
  )
}
